import React from 'react';
import { useLanguage } from '../../hooks/useLanguage';

interface CertificationWallpaperProps {
    load: number; // Nombre de fenêtres ouvertes
}

export const CertificationWallpaper: React.FC<CertificationWallpaperProps> = ({ load }) => {
    const { t } = useLanguage();
    
    return (
        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-950 via-slate-900 to-cyan-950 overflow-hidden">
            {/* Grille de certification avec motif plus élégant */}
            <div className="absolute inset-0 opacity-20">
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <pattern id="certGrid" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                            {/* Motif de certification - cercles concentriques */}
                            <circle cx="40" cy="40" r="30" fill="none" stroke="rgba(59, 130, 246, 0.3)" strokeWidth="1"/>
                            <circle cx="40" cy="40" r="20" fill="none" stroke="rgba(34, 211, 238, 0.2)" strokeWidth="1"/>
                            <circle cx="40" cy="40" r="10" fill="none" stroke="rgba(59, 130, 246, 0.4)" strokeWidth="1"/>
                            {/* Lignes diagonales */}
                            <line x1="0" y1="0" x2="80" y2="80" stroke="rgba(34, 211, 238, 0.15)" strokeWidth="0.5"/>
                            <line x1="80" y1="0" x2="0" y2="80" stroke="rgba(34, 211, 238, 0.15)" strokeWidth="0.5"/>
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#certGrid)" />
                </svg>
            </div>

            {/* Lignes de données élégantes */}
            <div className="absolute inset-0">
                {[...Array(4)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute h-px bg-gradient-to-r from-transparent via-blue-400/30 to-transparent opacity-40"
                        style={{
                            top: `${20 + i * 20}%`,
                            width: '100%',
                            animation: `dataFlow ${4 + i * 0.7}s ease-in-out infinite`,
                            animationDelay: `${i * 1.2}s`
                        }}
                    />
                ))}
            </div>

            {/* Badge de certification central visible */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-30">
                <svg width="400" height="400" viewBox="0 0 200 200">
                    {/* Fond opaque pour masquer le motif derrière */}
                    <circle cx="100" cy="100" r="90" fill="rgba(15, 23, 42, 0.9)"/>
                    
                    {/* Cercle extérieur */}
                    <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(59, 130, 246, 0.8)" strokeWidth="2"/>
                    <circle cx="100" cy="100" r="70" fill="none" stroke="rgba(34, 211, 238, 0.6)" strokeWidth="1"/>
                    
                    {/* Étoile centrale - Centrée dans le cercle */}
                    <path d="M100 60 L110 85 L135 85 L117 100 L125 125 L100 110 L75 125 L83 100 L65 85 L90 85 Z" 
                          fill="rgba(59, 130, 246, 0.4)" 
                          stroke="rgba(34, 211, 238, 0.6)" 
                          strokeWidth="1"/>
                    
                    {/* Texte CERTIFIED - Doré - Remonté */}
                    <text x="100" y="145" textAnchor="middle" fill="rgba(255, 193, 7, 0.8)" fontSize="14" fontWeight="bold" fontFamily="monospace">
                        CERTIFIED
                    </text>
                </svg>
            </div>

            {/* Texte de statut - Version certifiée */}
            <div className="absolute bottom-8 left-8 text-xs text-blue-400/70 font-mono space-y-1">
                <div className="text-cyan-400 font-bold">{t('certificationWallpaper.certified')}</div>
                <div>{t('certificationWallpaper.operatorStatus')}</div>
                <div>{t('certificationWallpaper.date')}</div>
            </div>

            {/* Badge "CERTIFIED" en haut à droite */}
            <div className="absolute top-8 right-8 text-xs text-blue-400/50 font-mono text-right space-y-1">
                <div className="text-cyan-400 font-bold text-sm">{t('certificationWallpaper.badge')}</div>
                <div className="text-[10px]">{t('certificationWallpaper.level')}</div>
            </div>

            {/* Effet de scan subtil */}
            <div 
                className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-400/5 to-transparent h-12 opacity-30"
                style={{
                    animation: 'scanLine 5s ease-in-out infinite'
                }}
            />

            {/* Vignette élégante */}
            <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/40" />
        </div>
    );
};
